import React, {useCallback, useEffect, useState} from "react";
import AziendaNavContainer from "../../features/azienda/AziendaNavContainer";
import {AziendaRecord} from "@commons/mapping/AziendaRecord";
import {useAppSelector} from "@hooks/redux-hooks/hooks";
import {selectAzienda} from "@commons/core.slice";
import {useGetElementiXSelezioneQuery} from "@commons/features/PVA/RML/elementoRMLXSelezione.api";
import AreaSelezioneElementiRML from "../lavoratori/retribuzione/comps/AreaSelezioneElementiRML";
import {CDate, PeriodoType} from "@commons/types";
import {FieldErrorsImpl, useForm} from "react-hook-form";
import {RangeOptions} from "../../features/lavoratore/RangeOptions";
import Banner, {bannerStyle} from "../../components/Banner";
import {ElementoRMLSelezionabileRecord} from "@commons/mapping/ElementoRMLSelezionabileRecord";
import {useQueryFilter, useSetIdAzienda, useSetIdAziendaMaster} from "../../components/dataTable/customHooks";
import {
    RMLLavoratorePVAParams,
    useGetRetribuzioniByElementiQuery,
    useLazyGetRetribuzioniByElementiQuery,
    useLazyGetSingoliElementiQuery
} from "@commons/features/PVA/RML/lavoratoreXSelezione.api";
import {MutationError, useEditor} from "../../core/editor";
import TabellaMensileRML from "../lavoratori/retribuzione/comps/TabellaMensilita";
import {DettaglioRetribuzioneRecord} from "@commons/mapping/DettaglioRetribuzioneRecord";
import DettaglioRetribuzione from "../lavoratori/retribuzione/comps/DettaglioRetribuzione";
import {LavoratoreRecord} from "@commons/mapping/LavoratoreRecord";
import Loading from "../../components/dataTable/Loading";
import TooltipEx from "../../components/Tooltip";
import {export2excel} from "../../features/export";
import PVAFilter from "./PVAFilter";
import {useGetFilterQuery, useUpdateFilterMutation} from "@commons/features/PVA/RML/PVAFilter.api";
import {PVAFormData} from "@commons/mapping/PVAFormData";
import {toastAsError} from "@utilities/toastAsError";
import {usePagination} from "@components/dataTable/usePagination";
import {IconCalendar} from "@tabler/icons-react";

type FormData = {
    periodo: PeriodoType,
    dataDa: CDate | null,
    dataA: CDate | null,
}

const RetribPage: React.FC = () => {
    const azienda: AziendaRecord | null = useAppSelector(selectAzienda);
    const idAziendaMaster = azienda ? azienda.idAziendaMaster : null;
    const idAzienda = azienda ? azienda!.idAzienda : null;

    const [retribOraria, setRetribOraria] = useState<boolean>(false);
    const [filterOpen, setFilterOpen] = useState<boolean>(false);
    const toggleRetribOraria = (v: boolean) => setRetribOraria(v);
    const toggleVariazioni = (v: boolean) => {
        setFilter(filter => ({
            ...filter, soloVariazioni: v
        }));
        setPage(1);
    }
    const [periodoApplicato, setPeriodoApplicato] = useState<boolean>(false);
    const [subsetElementiApplicato, setSubsetElementiApplicato] = useState<boolean>(false);

    // *******************************
    // Gestione form selezione periodo
    const defaultValues = {
        periodo: 'p' as PeriodoType,
        dataDa: null,
        dataA: null,
    };
    const currentValues: FormData = {...defaultValues};
    const hook = useForm<FormData>({defaultValues: currentValues});

    const periodo: PeriodoType = hook.watch('periodo');
    const dataDa: CDate | null = hook.watch('dataDa');
    const dataA: CDate | null = hook.watch('dataA');

    useEffect(() => {
        setPeriodoApplicato(false);
    }, [periodo, dataDa, dataA]);

    // Applica intervallo
    function onHandleSubmit(newValues: FormData) {
        //console.log(newValues);
        setPeriodoApplicato(true);
    }

    // *******************************

    const onInvalid = (err: Partial<FieldErrorsImpl<FormData>> | undefined) => {
        console.log(err);
    }

    // **********************************************************************************
    // query per la selezione degli elementi retributivi presenti nel periodo selezionato
    const {data: elementiRetributivi, error, isLoading} = useGetElementiXSelezioneQuery({
        idAziendaMaster,
        idAzienda,
        dataDa,
        dataA,
    }, {
        skip: !azienda || !periodoApplicato,
        refetchOnMountOrArgChange: true
    });

    // ************************************************************************************
    // query per il dettaglio degli elementi retributivi del lavoratore/periodo selezionato
    const [getTriggerDetail] = useLazyGetSingoliElementiQuery();
    const {resource, cancel, URI, loadResource} = useEditor<DettaglioRetribuzioneRecord>(undefined, getTriggerDetail, undefined, undefined)

    // Pagination per query elenco lavoratori/rml
    const pagination = usePagination('', 1, 10);
    const setPage = pagination.setPage;
    // Cancella tutte le selezioni
    const clearAll = useCallback((): void => {
        // resetto lista elelementi al cambio azienda!
        setPeriodoApplicato(false);
        setSubsetElementiApplicato(false);
        cancel(); // Chiudo eventuale editing della risorsa...
        setPage(1);
    }, [cancel, setPage]);

    // Cancella la selezione dipendenti dagli elementi e dal filtro
    const handleChangeSubset = useCallback((elementi?: ElementoRMLSelezionabileRecord[]): void => {
        setSubsetElementiApplicato(false);
        cancel(); // Chiudo eventuale editing della risorsa...
        setPage(1);
    }, [cancel, setPage]);

    // Filtro per query elenco lavoratori/rml
    const {filter, setFilter, skip} = useQueryFilter<RMLLavoratorePVAParams>({} as RMLLavoratorePVAParams, pagination);
    useSetIdAziendaMaster(azienda, setFilter);
    useSetIdAzienda(azienda, setFilter, clearAll);

    // ********************************************************************************************************************************
    // query per l'elenco dei lavoratori e delle relative rml rispondenti alla la selezione degli elementi retributivi e del filtro PVA
    const {
        data: retribuzioneByElementi, isFetching: isFetchingRetribuzioni,
    } = useGetRetribuzioniByElementiQuery(filter, {
        skip: (!skip && filter && filter.idAziendaMaster && filter.idAzienda && subsetElementiApplicato) ? false : true,
        refetchOnMountOrArgChange: true,
    })

    // ********************************************************************************************************
    // Gestione filtro PVA
    // Query per ripristinare l'ultimo filtro utilizzato (relativo alla sessione corrente)
    const {data: lastPvaFilter, isLoading: isLoadingLastPvaFilter} = useGetFilterQuery({idAziendaMaster}, {skip: !azienda});
    // Mutation per update del filtro
    const [updatePvaFilter, updatePvaFilterResult] = useUpdateFilterMutation();
    // Gestione del form
    const hookPvaFilter = useForm<PVAFormData>({defaultValues: PVAFormData.factory()});
    const reset = hookPvaFilter.reset;
    useEffect(() => {
        if (lastPvaFilter)
            reset(PVAFormData.factory(lastPvaFilter)); // Imposto ultimo filtro della sessione
    }, [reset, lastPvaFilter]);

    const onResetPvaFilter = () => {
        reset(PVAFormData.factory()); // Reset del filtro
        handleChangeSubset(); // Nascondo tabella risultato (retribuzioneByElementi)
    }

    const handleSubmit = hookPvaFilter.handleSubmit;
    const handleApply = useCallback((): void => {
        handleSubmit((pvaFormdata) => {
            //console.log(pvaFormdata);

            updatePvaFilter({...pvaFormdata, idAziendaMaster})
                .unwrap()
                .then((data) => {
                    //toast('Filtro aggiornato!', {type: 'success'})
                    reset(pvaFormdata); // Consolido form con nuovi valori! (reset isDirty!!!)
                    setFilterOpen(false); // Chiudo accordion filtro PVA
                    setSubsetElementiApplicato(true); // Ripristino tabella risultato
                })
                .catch((error: MutationError) => {
                    // @ts-ignore
                    const message = error.data.message;
                    toastAsError(message);
                })
        })();
    }, [handleSubmit, updatePvaFilter, idAziendaMaster, reset]);

    // Al variare del form, Nascondo tabella risultato
    useEffect(() => {
        if (hookPvaFilter.formState.isDirty) {
            handleChangeSubset();
        }
    }, [hookPvaFilter.formState.isDirty, handleChangeSubset]);

    const soloVariazioni = (filter && filter.soloVariazioni) ? filter.soloVariazioni : false;

    // **************************************
    // query per export risultato corrente...
    const [exportTrigger, {isLoading: exportIsLoading, isFetching: exportIsFetching}] = useLazyGetRetribuzioniByElementiQuery();
    const exportRML = () => export2excel(exportTrigger, filter, 'Lavoratori & RML');

    return (
        <Loading loading={exportIsLoading || exportIsFetching}>
            <AziendaNavContainer>
                {azienda && <>
                    <div className="px-2 pt-2">

                        <form onSubmit={hook.handleSubmit(onHandleSubmit, onInvalid)}>
                            <div className="divide-y divide-solid">
                                <div className="bg-white rounded-md px-2 md:px-6 w-full max-sm:w-full">
                                    <div className="py-2">
                                        Periodo di riferimento per la selezione degli elementi retributivi:
                                    </div>

                                    <div className="py-2">

                                        <RangeOptions options={['m', 'b', 't', 'q', 's', 'cy', 'fy', 'custom', 'p']}
                                                      {...hook} radioGroupName={'periodo'}
                                                      enabled={true}
                                                      dataDa={currentValues.dataDa}
                                                      dataDaName={'dataDa'}
                                                      dataA={currentValues.dataA}
                                                      dataAName={'dataA'}/>

                                    </div>
                                </div>
                                <div className="mt-5 pt-5 w-full border-t border-gray-200">
                                    <div className="flex flex-row justify-end">
                                        <button type="submit"
                                                className="ml-3 inline-flex justify-center rounded-md border border-transparent bg-blue-600 p-1.5 sm:p-2 md:py-2 md:px-4 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
                                            Applica intervallo
                                            <IconCalendar className="ml-2 -mr-0.5 h-5 w-5" aria-hidden="true"/>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </form>

                        {!periodoApplicato && <Banner style={bannerStyle.warning} text="Seleziona un periodo e/o premi il bottone Applica intervallo!"/>}
                        {periodoApplicato &&
                            <>
                                <div className="mt-3">
                                    <AreaSelezioneElementiRML isLoading={isLoading} data={elementiRetributivi} onApply={handleApply} onChange={handleChangeSubset}
                                                              onResetFilter={onResetPvaFilter}
                                    >
                                        <div className="mt-5 w-full my-1 rounded-md bg-white shadow">
                                            <PVAFilter idAzienda={azienda.idAziendaCDIC!}
                                                       open={filterOpen}
                                                       setOpen={setFilterOpen}
                                                       useFormReturn={hookPvaFilter}
                                            ></PVAFilter>
                                        </div>
                                    </AreaSelezioneElementiRML>
                                </div>

                                {!subsetElementiApplicato && <Banner style={bannerStyle.warning} text="Seleziona uno o più elementi e premi il bottone Applica selezione!"/>}
                                {subsetElementiApplicato &&
                                    <div className="p-3">


                                        {/* DETTAGLIO DEGLI ELEMENTI RETRIBUTIVI DEL LAVORATORE/PERIODO SELEZIONATO */}
                                        {resource && <div className="p-3 bg-white rounded-md">
                                            <DettaglioRetribuzione
                                                mostraNominativo
                                                dettagliContratto={LavoratoreRecord.factory({
                                                    descrizione: resource.nominativo,
                                                    dataAssunzione: resource.dataAssunzione,
                                                    dataAssConvenzionale: resource.dataAssConvenzionale,
                                                    dataFineTDET: resource.dataFineTDET,
                                                    dataCessazione: resource.dataCessazione,
                                                })}
                                                dettagliRetribuzione={resource}
                                                onCancel={cancel}
                                                mostraRetribOraria={retribOraria}
                                                toggleRetribOraria={toggleRetribOraria}/>
                                        </div>}

                                        {/* TABELLA DEI LAVORATORI E RML RISULTANTI DALLA SELEZIONE */}
                                        <Loading loading={isFetchingRetribuzioni}>
                                            <TabellaMensileRML
                                                title="Lavoratori e relative RML"
                                                datasource={retribuzioneByElementi}
                                                pagination={pagination}
                                                retribOraria={retribOraria}
                                                toggleRetribOraria={toggleRetribOraria}
                                                soloVariazioni={soloVariazioni}
                                                toggleVariazioni={toggleVariazioni}
                                                URI={URI}
                                                loadResource={loadResource}
                                                showNominativo
                                                showSommaVoci
                                            />
                                        </Loading>

                                        <div className="mt-3 flex flex-row justify-end">
                                            <TooltipEx tooltip={`Esporta in un file XLSX l'elenco dei lavoratori con le relative RML`}>
                                                <button
                                                    type="button"
                                                    className="bg-blue-600 btnPrimary"
                                                    onClick={exportRML}
                                                    disabled={exportIsLoading || exportIsFetching}
                                                >
                                                    Export
                                                </button>
                                            </TooltipEx>
                                        </div>
                                    </div>
                                }
                            </>}

                    </div>
                </>}
            </AziendaNavContainer>
            <div data-iframe-height=""></div>
        </Loading>
    )
}
export default RetribPage;